import { DatalayerDataAttributes } from '@/datalayer/App.datalayer.constants';
import {
  eleIsLinkDownloadType,
  getDatalayerAttr,
  getLinkType,
} from '@/datalayer/App.datalayer.utils';
import { mapTargetBySelectors } from '@/features/shared/HOCs/withGTM';
import { OnDatalayerProps } from '@/features/shared/HOCs/withGTM.interface';
import {
  isEmailLink,
  isExternalLinkContent,
  isInternalEmailLink,
  isURLWithFile,
  matchExternalLink,
} from '@/utils/helpers';

/* GENERIC LINK_INTERNAL, LINK_EXTERNAL, FILE_DOWNLOAD DATA-LAYER LOGIC */

const handleInternalLinkEvent = ({
  target,
  position = '',
  variant = '',
}: {
  target: HTMLAnchorElement;
  position?: string;
  variant?: string;
}) => {
  const href = target?.href || target?.dataset?.datalayerLinkHref || '';
  const { textContent, ariaLabel } = target;
  const container = target?.closest('[data-datalayer-position]') as HTMLDivElement;
  const type = getLinkType(target);
  const variantName = variant || container?.dataset?.datalayerVariant || '';
  const containerTitle = target?.closest('[data-datalayer-title]') as HTMLDivElement;
  const title = containerTitle?.dataset?.datalayerTitle || '';
  const label = textContent?.trim() || ariaLabel?.trim();

  return {
    event: 'trackEvent-internalLinkClick',
    event_name: 'internal_link_click',
    eventCategory: 'engagement',
    eventAction: 'internal_link_click',
    eventLabel: label,
    position: position || container?.dataset?.datalayerPosition || '',
    ...(!!href && { link_url: href }),
    link_text: label,
    ...(title && { title }),
    type,
    ...(!!variantName && { variant_name: variantName }),
  };
};

const handleExternalLinkEvent = ({
  target,
  position = '',
  variant = '',
}: {
  target: HTMLAnchorElement;
  position?: string;
  variant?: string;
}) => {
  const href = target?.href || target?.dataset?.datalayerLinkHref || '';
  const url = new URL(href);
  const { textContent } = target;
  const container = target?.closest('[data-datalayer-position]') as HTMLDivElement;
  const containerTitle = target?.closest('[data-datalayer-title]') as HTMLDivElement;
  const title = containerTitle?.dataset?.datalayerTitle || '';
  const type = getLinkType(target);
  const variantName = variant || container?.dataset?.datalayerVariant || '';

  return {
    event: 'trackEvent-externalLinkClick',
    event_name: 'click',
    eventCategory: 'engagement',
    eventAction: 'external_link_click',
    eventLabel: textContent?.trim(),
    position: position || container?.dataset?.datalayerPosition || '',
    ...(!!variantName && { variant_name: variantName }),
    ...(title && { title }),
    ...(!!url.host && { link_domain: url.host }),
    ...(!!href && { link_url: href }),
    link_text: textContent?.trim(),
    type,
  };
};

const handleDownloadLinkEvent = ({
  target,
  position = '',
  variant = '',
}: {
  target: HTMLAnchorElement;
  position?: string;
  variant?: string;
}) => {
  try {
    const getDownloadAttr = getDatalayerAttr(target);

    const href = target?.href || getDownloadAttr(DatalayerDataAttributes.DATALAYER_HREF) || '';
    const fullFilename = (href.match(/[^/]+(?=\/$|$)/) || [''])[0];
    const fileExtension = fullFilename.split('.')[1];

    if (!href) throw new Error('An href or data-datalayer-href prop should be provided');

    const url = new URL(href);
    const { textContent } = target;
    const containerTitle = target?.closest('[data-datalayer-title]') as HTMLDivElement;
    const title = containerTitle?.dataset?.datalayerTitle || '';
    const container = target?.closest('[data-datalayer-position]') as HTMLDivElement;
    const type = getLinkType(target);
    const variantName = variant || container?.dataset?.datalayerVariant || '';

    return {
      event: 'trackEvent-fileDownload',
      event_name: 'file_download',
      eventCategory: 'engagement',
      eventAction: 'file_download',
      eventLabel: fullFilename?.trim(),
      position: position || container?.dataset?.datalayerPosition || '',
      ...(!!variantName && { variant_name: variantName }),
      link_text: textContent?.trim() || title,
      ...(!!url.host && { link_domain: url.host }),
      ...(!!href && { link_url: href }),
      ...(!!title && { title }),
      file_name: fullFilename,
      file_extension: fileExtension,
      type,
    };
  } catch (e) {
    console.error('We could not complete the handleDownloadLinkEvent process =>', e);
    return undefined;
  }
};
export const linkStrategy = ({
  target,
  position = '',
  variant = '',
}: {
  target: HTMLAnchorElement;
  position?: string;
  variant?: string;
}) => {
  const href = target?.getAttribute('href') || '';

  if (
    !target?.getAttribute('data-datalayer-link') &&
    (!href || /^#/.test(href)) &&
    !target?.getAttribute('data-datalayer-link-type')
  )
    return undefined;

  if (isURLWithFile(href) || eleIsLinkDownloadType(target))
    return handleDownloadLinkEvent({ target, position, variant });

  if (isExternalLinkContent(href)) return handleExternalLinkEvent({ target, position, variant });

  return handleInternalLinkEvent({ target, position, variant });
};

export enum LinkSelectors {
  Event = 'a.MuiLink-root[data-datalayer-link-type]',
  Link = 'a:not(.MuiPaginationItem-root):not(.a2a_button_facebook):not(.a2a_button_x):not(.a2a_button_whatsapp):not(.a2a_dd)',
  Jumplink = 'button.MuiButton-root[data-datalayer-link-type]',
  Custom = '*[data-datalayer-link]',
}

export const mapTargetLink = mapTargetBySelectors([
  LinkSelectors.Link,
  LinkSelectors.Jumplink,
  LinkSelectors.Custom,
]);

export const mapTargetNonRepetitiveLink = mapTargetBySelectors([
  `${LinkSelectors.Link}:not(.MuiButton-root)`,
  LinkSelectors.Jumplink,
  LinkSelectors.Custom,
]);

export const onDatalayer = ({ target }: OnDatalayerProps) => {
  if (target?.getAttribute('data-datalayer-ignore-link')) return undefined;
  if (target?.closest('[data-datalayer-ignore-link-section]')) return undefined;

  return linkStrategy({ target });
};

/* CTA DATA-LAYER LOGIC INCLUDING LINKS AND EVENTS */

const onDataLayerPushCTA = ({
  target,
  position = '',
  type = '',
  variant = '',
}: {
  target: HTMLAnchorElement;
  position?: string;
  type?: string;
  variant?: string;
}) => {
  const { textContent } = target;
  const container = target?.closest('[data-datalayer-position]') as HTMLDivElement;
  const variantName = variant || container?.dataset?.datalayerVariant || '';
  const containerTitle = target?.closest('[data-datalayer-title]') as HTMLDivElement;
  const title = containerTitle?.dataset?.datalayerTitle || '';

  let linkUrl = '';
  let linkDomain = '';

  if (target?.href || target?.dataset?.datalayerLinkHref) {
    linkUrl = target?.href || target?.dataset?.datalayerLinkHref || '';
    linkDomain = matchExternalLink(linkUrl) ? new URL(linkUrl).host : window.location.host;
  }

  return {
    event: 'trackEvent-ctaClicked',
    event_name: 'cta_click',
    eventCategory: 'engagement',
    eventAction: 'cta_click',
    eventLabel: textContent?.trim(),
    position: position || container?.dataset?.datalayerPosition || '',
    ...(!!variantName && { variant_name: variantName }),
    ...(!!linkDomain && { link_domain: linkDomain }),
    ...(!!linkUrl && { link_url: linkUrl }),
    ...(!!title && { title }),
    link_text: textContent?.trim(),
    type,
  };
};

export enum CTASelectors {
  Event = 'button.MuiButton-root[data-datalayer-cta-type]',
  Link = 'a.MuiButton-root',
  NewsRibbon = '.gtm-news-ribbon-click a[data-datalayer-cta]',
  Custom = '*[data-datalayer-cta], *[data-datalayer-cta-type]',
}

export const mapTargetCTA = mapTargetBySelectors([
  CTASelectors.Event,
  CTASelectors.Link,
  CTASelectors.NewsRibbon,
  CTASelectors.Custom,
]);

const getTypeCTA = (target: HTMLAnchorElement) => {
  // Button (Event). popup_form and link_to_scroll.
  if (
    target?.getAttribute('data-datalayer-cta-type') ||
    target?.getAttribute('data-datalayer-type')
  )
    return (
      target?.getAttribute('data-datalayer-cta-type') || target?.getAttribute('data-datalayer-type')
    );

  // Anchor (Link). internal_link, external_link and file_download.
  if (/a/.test(target?.localName)) {
    const href = target?.getAttribute('href') || '';

    if (!href || /^#/.test(href)) return '';
    if (isURLWithFile(href)) return 'file_download';
    if (isExternalLinkContent(href)) return 'external_link';
    if (isEmailLink(href) && isInternalEmailLink(href)) return 'internal_mail_link';
    if (isEmailLink(href) && !isInternalEmailLink(href)) return 'external_mail_link';

    return 'internal_link';
  }

  return 'non_identified';
};

export const linkStrategyCTA = ({
  target,
  position = '',
  variant = '',
}: {
  target: HTMLAnchorElement;
  position?: string;
  variant?: string;
}) => {
  const type = getTypeCTA(target);
  if (!type) return undefined;

  return onDataLayerPushCTA({ target, position, type, variant });
};

export const onDataLayerCTA = ({ target }: OnDatalayerProps) => {
  if (target?.getAttribute('data-datalayer-ignore-cta')) return undefined;
  if (target?.closest('[data-datalayer-ignore-cta-section]')) return undefined;

  return linkStrategyCTA({ target });
};
